import { Box, Button, Dialog, Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import OTPForm from '../../lib/formik/OtpForm';
import { SuccessIcon } from '../../assets/SvgIcons';
import { showError } from '../../redux-store/actions/auth';
import BreadCrum from '../../lib/BreadCrum';
import {
    subbrokerValidateOtp,
    subbrokerResendOtp,
    subbrokerSelfDeclaration
} from './actions';
import {
    SubbrokerValidateOtpResponseBody,
    SubbrokerResendOtpResponseBody,
    selfReclarationResponseBody
} from './types';

interface LocationState {
    id: number;
    token: string;
    message: string;
    numberOfOTPTries: number;
    payload: {
        productDeclaration: {
            productType: string;
            isSelfDeclared: boolean;
            version?: string;
        }[];
    };
}

export default function VerifySubbrokerOtp(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();

    const locationState = history.location.state as LocationState;
    const { id, token, message, numberOfOTPTries, payload } = locationState || {};

    const [otpMessage, setOtpMessage] = useState<string>(message ?? '');
    const [numberOfOtpTries, setNumberOfOtpTries] = useState<number>(numberOfOTPTries ?? 0);
    const [currentToken, setCurrentToken] = useState<string>(token ?? '');
    const [currentId, setCurrentId] = useState<number>(id ?? 0);
    const [successPopup, setSuccessPopup] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    // const breadcrumbPath = [
    //     { name: 'Dashboard', path: '/dashboard', warning: false },
    //     { name: 'My Profile', path: '/my-profile' },
    //     { name: 'Verify OTP', path: '/my-profile/otp-screen' },
    // ];

    const handleSubmit = async (otp: string) => {
        try {
            const validateRes = await (dispatch(subbrokerValidateOtp({
                otp,
                token: currentToken,
                id: currentId,
            })) as unknown as Promise<SubbrokerValidateOtpResponseBody>);

            if (!validateRes?.isSuccess) {
                dispatch(showError(validateRes?.data?.message ?? validateRes?.message ?? 'OTP validation failed'));
                return;
            }
            //self declaration post api - pass otpRef as second parameter
            const selfDeclareRes = await (dispatch(subbrokerSelfDeclaration(payload, validateRes?.data?.id)) as unknown as Promise<selfReclarationResponseBody>);

            if (!selfDeclareRes?.isSuccess) {
                dispatch(showError(selfDeclareRes?.message ?? 'Self Declaration failed'));
                return;
            }
            setSuccessMessage(selfDeclareRes?.message ?? 'Self Declaration submitted successfully');
            setSuccessPopup(true);

        } catch (e: any) {
            dispatch(showError(e?.message ?? 'Something went wrong'));
        }
    };

    const handleResend = async () => {
        try {
            const response = await (dispatch(subbrokerResendOtp({
                token: currentToken,
                id: currentId,
            })) as unknown as Promise<SubbrokerResendOtpResponseBody>);

            if (response?.isSuccess) {
                setOtpMessage(response?.data?.message ?? '');
                setNumberOfOtpTries(response?.data?.numberOfOTPTries ?? numberOfOTPTries );
                setCurrentToken(response?.data?.token ?? currentToken);
                setCurrentId(response?.data?.id ?? currentId);
              //updates history data
                history.replace({
                    pathname: '/my-profile/otp-screen',
                    state: {
                        ...locationState,
                        id: response?.data?.id,
                        token: response?.data?.token,
                        message: response?.data?.message,
                        numberOfOTPTries: response?.data?.numberOfOTPTries,
                    },
                });
            } else {
                dispatch(showError(response?.data?.message ?? response?.message ?? 'Failed to resend OTP'));
            }
        } catch (e: any) {
            dispatch(showError(e?.message ?? 'Failed to resend OTP'));
        }
    };

    const handleSuccessClose = () => {
        setSuccessPopup(false);
        history.replace('/my-profile');
    };

    return (
        <>
            {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BreadCrum array={breadcrumbPath} />
            </Box> */}
            <Box sx={{ padding: { xs: '10px 10px', sm: '30px 45px' } }}>
                <Typography
                    variant="h1"
                    sx={(theme: Theme) => ({
                        lineHeight: { xs: '20px', sm: '32.5px' },
                        color: theme.palette.text.primary,
                    })}
                >
                    Verify Self Declaration
                </Typography>
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: '25px',
                        borderRadius: '20px',
                        backgroundColor: theme.palette.common.white,
                        padding: { xs: '10px', sm: '50px' },
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    })}
                >
                    <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                        <OTPForm
                            onSubmit={handleSubmit}
                            onResend={handleResend}
                            otpMessage={otpMessage}
                            warningMessage=""
                            numberOfTries={numberOfOtpTries}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Success Dialog */}
            <Dialog
                onClose={handleSuccessClose}
                open={successPopup}
                maxWidth="sm"
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: '24px',
                        width: '533px',
                    },
                })}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '48px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingTop: '15px',
                            rowGap: '12px',
                        }}
                    >
                        <SuccessIcon />
                        <Typography
                            variant="subtitle_400"
                            sx={(theme: Theme) => ({
                                lineHeight: '20px',
                                letterSpacing: '0.02px',
                                color: theme.palette.text.secondary,
                                textAlign: 'center',
                            })}
                        >
                            {successMessage}
                        </Typography>
                    </Box>
                    <Box>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={(theme: Theme) => ({
                                borderRadius: '4px',
                                padding: '13px 20px',
                                backgroundColor: theme.palette.primary[500],
                                ...theme.typography.body_600,
                                fontSize: '16px',
                                lineHeight: '20px',
                            })}
                            onClick={handleSuccessClose}
                        >
                            Go To My Profile
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
}
